import { useAuth } from "@clerk/clerk-react";
import {
  Box,
  Button,
  CircularProgress,
  FormHelperText,
  Grid,
  Link,
  TextField,
} from "@mui/material";
import { CheckoutContext } from "contexts/Checkout";
import { OrganizationUserContext } from "contexts/Organization";
import { useContext, useEffect, useState } from "react";
import { Plans } from "schemas/payments";

import { LIVE_COUPON_CODE } from "constants/influencer";
import { fetcherAuth } from "utils/api";
import { trackEvent } from "utils/tracking";

import { StripePromoCode } from "../schema";
import { useCheckout } from "../useCheckout";

interface Props {
  productId: string;
}

const PromotionalCode = ({ productId }: Props) => {
  const { coupon, plan, setCoupon } = useContext(CheckoutContext);
  const { applyPromoCode } = useCheckout();

  const { getToken } = useAuth();
  const { currentOrg } = useContext(OrganizationUserContext);
  const [renderTextField, setRenderTextField] = useState(false);
  const addPromotionalCode = () => {
    trackEvent("Subscriptions Page - Add Promotional Code Button Clicked");
    setRenderTextField(true);
  };
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [promoCodeName, setPromoCodeName] = useState("");

  const handleApplyPromoCode = async () => {
    if (!currentOrg) return;
    if (!promoCodeName && !coupon) {
      setError("Please enter a valid code");
      return;
    }
    setLoading(true);
    try {
      const { promotionalCode } = await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg?.id}/payments/promo-code`,
        "POST",
        {},
        {
          couponCode: promoCodeName || coupon,
          planName: plan,
        },
      );

      const promoCodeErrorMessage = validatePromotionalCode(promotionalCode);
      if (promoCodeErrorMessage) {
        setError(promoCodeErrorMessage);
        return;
      }
      await applyPromoCode({
        id: promotionalCode.id,
        name: promotionalCode.coupon?.name,
      });
      setError("");
    } catch (error) {
      setError(error?.message);
    } finally {
      setLoading(false);
    }
  };

  const validatePromotionalCode = (promotionalCode: StripePromoCode) => {
    if (coupon && promotionalCode?.coupon?.metadata?.organization_id) {
      if (
        currentOrg?.id !==
        Number(promotionalCode?.coupon?.metadata?.organization_id)
      ) {
        return "Invalid promotional code";
      }
    }
    if (!promotionalCode) {
      return "No promotional code found";
    }
    if (!promotionalCode?.active) {
      return "Promotional code has expired";
    }

    if (
      productId?.length > 0 &&
      promotionalCode?.coupon?.applies_to?.products &&
      promotionalCode?.coupon?.applies_to?.products?.length > 0
    ) {
      const includesProductId =
        promotionalCode?.coupon?.applies_to?.products?.includes(productId);
      if (!includesProductId) {
        return "Promotional code is not valid for this product";
      }
      return "";
    }
  };

  useEffect(() => {
    if (coupon?.includes(LIVE_COUPON_CODE)) {
      if (plan === Plans.VA_YEARLY) {
        handleApplyPromoCode();
      } else {
        setCoupon("");
      }
    } else if (coupon && !renderTextField) {
      setRenderTextField(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [coupon, plan]);

  return renderTextField ? (
    <Grid item xs>
      <Grid container alignItems="center" gap={1} justifyContent="flex-end">
        <TextField
          size="small"
          placeholder="Promotional Code"
          onChange={(e) => setPromoCodeName(e.target.value)}
          variant="standard"
          defaultValue={coupon || ""}
          sx={{
            input: {
              fontSize: 12,
              "&::placeholder": {
                fontSize: 12,
              },
            },
          }}
          error={!!error}
          helperText={error}
        />
        <Link
          onClick={handleApplyPromoCode}
          sx={{
            textTransform: "none",
            textDecoration: "none",
            fontSize: 12,
            cursor: "pointer",
            fontWeight: 700,
          }}
        >
          {loading ? <CircularProgress size={10} /> : "Apply"}
        </Link>
        <Link
          sx={{
            textTransform: "none",
            textDecoration: "none",
            color: "secondary.main",
            fontSize: 12,
            cursor: "pointer",
          }}
          onClick={() => {
            setRenderTextField(false);
            setPromoCodeName("");
            setError("");
          }}
        >
          Cancel
        </Link>
      </Grid>

      <Grid container justifyContent={"flex-end"}>
        {coupon && !promoCodeName && (
          <FormHelperText sx={{ color: "purple.main" }}>
            <Box
              sx={{ mr: 0.5 }}
              component="i"
              className="fa-solid fa-circle-info"
            />
            <strong>Use your coupon code before it expires!</strong>
          </FormHelperText>
        )}
      </Grid>
    </Grid>
  ) : (
    <Button
      size="small"
      sx={{
        textTransform: "none",
        color: "purple.main",
        fontSize: { xs: 10, md: 12 },
        mr: 2,
        mt: -1,
      }}
      onClick={addPromotionalCode}
      disableElevation
    >
      + Promo Code
    </Button>
  );
};

export default PromotionalCode;
